import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import {
  checkedKing,
  currentCycle,
  dueCards,
  dueWithin,
  explainSan,
  hintOf,
  isPromotion,
  localDay,
  logAttempt,
  pickNewPuzzle,
  playMove,
  recordCard,
  recordWoodpecker,
  solutionFrom,
  startPuzzle,
  streakDays,
  targetsOf,
  themeOf,
  unlockedThemes,
  updateRating,
  type CardProgress,
  type DayLog,
  type MistakeReason,
  type Puzzle,
  type PuzzleState,
  type WoodpeckerCycle,
} from '@familyhub/shared';
import { Sheet } from '../platform/Sheet';
import { Board, pieceName, type Arrow } from './Board';
import { saveCard, saveCycle, saveProfile, type SchaakState } from './actions';
import { freshMistakes, mistakePuzzle, plural, shortDate, type Mode, type SessionSummary } from './common';
import type { GameRecord, MistakeRecord } from './db';
import { WOODPECKER_SET_NO, type Pack } from './pack';
import { MistakeReview } from './Review';

/**
 * A training session (docs/SCHAAK.md §3.1–3.2): one puzzle after another,
 * chosen by the mode — Trainen (due → his own mistakes → new puzzles), one
 * theme, his mistakes (all, or one game's), or the Woodpecker set. Every result
 * is an optimistic local write replayed through the outbox, and is reported up
 * (`onAttempt`) so the screen can show a summary when the session stops.
 */

/** How one puzzle went. `newRating` is set only for a rated (first) attempt. */
type Outcome = { solved: boolean; newRating: number | null };

type Current = { p: Puzzle; review: boolean; mistake?: MistakeRecord };

export function Session({
  mode,
  state,
  pack,
  userId,
  onStop,
  onAttempt,
}: {
  mode: Exclude<Mode, { kind: 'notation' | 'notation-lesson' | 'notation-drill' | 'summary' }>;
  state: SchaakState;
  pack: Pack;
  userId: string;
  onStop: () => void;
  onAttempt: (label: string, solved: boolean, rating: number) => void;
}) {
  const seen = useRef(new Set<string>());
  // What this session last wrote. The live query catches up a moment after a
  // write, and the next puzzle can be finished before it does; reading the
  // profile, the cycle or a card from a stale state would count one answer twice.
  const local = useRef<{
    profile?: { r: number; g: number; days: DayLog[] };
    cycle?: WoodpeckerCycle;
    cards: Map<string, CardProgress>;
  }>({ cards: new Map() });
  const profile =
    local.current.profile && local.current.profile.g >= state.ratingGames
      ? local.current.profile
      : { r: state.rating, g: state.ratingGames, days: state.days };
  const cycles = mergeCycle(state.cycles, local.current.cycle);
  const [tally, setTally] = useState({ done: 0, solved: 0 });
  // Which step of the session this is — the Solve key. Not the tally: that
  // moves the moment a puzzle ends, and would reset the board under the verdict.
  const [step, setStep] = useState(0);
  // Chosen once per step and held, so our own write landing never swaps it mid-solve.
  const [current, setCurrent] = useState<Current | null | undefined>(undefined);
  const [reason, setReason] = useState<MistakeReason | null>(null);
  const cycle = useMemo(
    () => (mode.kind === 'woodpecker' ? currentCycle(cycles, WOODPECKER_SET_NO, new Date()) : null),
    [mode.kind, cycles],
  );

  const choose = useCallback(() => {
    const now = new Date();
    if (mode.kind === 'woodpecker') {
      const c = currentCycle(cycles, WOODPECKER_SET_NO, now);
      const p = c ? pack.woodpecker[c.position] : undefined;
      setCurrent(p ? { p, review: false } : null);
      return;
    }
    const mistakes = new Map(state.mistakes.map((m) => [m.id, m]));
    const asMistake = (m: MistakeRecord, review: boolean) => setCurrent({ p: mistakePuzzle(m), review, mistake: m });
    // One game: all its mistakes in order, practised or not.
    if (mode.kind === 'mistakes' && mode.gameId) {
      const next = state.mistakes
        .filter((m) => m.game_id === mode.gameId && !seen.current.has(m.id))
        .sort((a, b) => a.ply - b.ply)[0];
      if (next) asMistake(next, !!state.cards[next.id]);
      else setCurrent(null);
      return;
    }
    // Order: what is due, then his own unpractised mistakes — the best puzzles
    // there are — then new puzzles. One theme, from its sheet, is puzzles only.
    const themes = mode.kind === 'train' ? (mode.themes ?? unlockedThemes(state.cards)) : [];
    const want = new Set(themes);
    const withMistakes = mode.kind === 'mistakes' || (mode.kind === 'train' && !mode.themes);
    const due = dueCards(state.cards, now).find(
      (c) =>
        !seen.current.has(c.ref) &&
        ((mode.kind === 'train' && c.kind === 'puzzle' && !!c.theme && want.has(c.theme) && pack.byId.has(c.ref)) ||
          (withMistakes && c.kind === 'mistake' && mistakes.has(c.ref))),
    );
    if (due) {
      if (due.kind === 'mistake') asMistake(mistakes.get(due.ref)!, true);
      else setCurrent({ p: pack.byId.get(due.ref)!, review: true });
      return;
    }
    if (withMistakes) {
      const fresh = freshMistakes(state).find((m) => !seen.current.has(m.id));
      if (fresh) return asMistake(fresh, false);
    }
    if (mode.kind === 'mistakes') return setCurrent(null);
    const p = pickNewPuzzle(pack.puzzles, state.cards, themes, profile.r, Math.random, seen.current);
    setCurrent(p ? { p, review: false } : null);
  }, [mode, state, pack, cycles, profile.r]);

  useEffect(() => {
    if (current === undefined) choose();
  }, [current, choose]);

  const game = current?.mistake ? state.games.find((g) => g.id === current.mistake!.game_id) : undefined;
  const label = !current
    ? ''
    : mode.kind === 'woodpecker'
      ? `Woodpecker · cyclus ${cycle?.cycle ?? ''} · ${(cycle?.position ?? 0) + 1}/${pack.woodpecker.length}`
      : current.mistake
        ? `${current.review ? 'Herhaling · ' : ''}Uit je partij${game ? ` tegen ${game.opponent} · ${shortDate(game.played_at)}` : ''}`
        : `${current.review ? 'Herhaling · ' : ''}${themeOf(current.p.theme)?.label ?? ''} · ${current.p.rating}`;

  async function finish(c: Current, o: Outcome, seconds: number) {
    const { p, mistake } = c;
    seen.current.add(p.id);
    setTally((t) => ({ done: t.done + 1, solved: t.solved + (o.solved ? 1 : 0) }));
    const now = new Date();
    const r = o.newRating ?? profile.r;
    const g = profile.g + (o.newRating != null ? 1 : 0);
    const days = logAttempt(profile.days, localDay(now), r, o.solved);
    local.current.profile = { r, g, days };
    onAttempt(label, o.solved, r);
    if (mode.kind === 'woodpecker') {
      const cy = currentCycle(cycles, WOODPECKER_SET_NO, now);
      if (cy) {
        const next = recordWoodpecker(cy, o.solved, seconds, pack.woodpecker.length, now);
        local.current.cycle = next;
        await saveCycle(userId, next);
      }
    } else {
      const card = recordCard(
        local.current.cards.get(p.id) ?? state.cards[p.id],
        mistake ? 'mistake' : 'puzzle',
        p.id,
        p.theme,
        o.solved,
        now,
      );
      local.current.cards.set(p.id, card);
      await saveCard(userId, card);
    }
    await saveProfile(userId, r, g, days);
  }

  async function saveReason(id: string, r: MistakeReason) {
    setReason(r);
    const card = local.current.cards.get(id) ?? state.cards[id];
    if (!card) return;
    const next = { ...card, reason: r };
    local.current.cards.set(id, next);
    await saveCard(userId, next);
  }

  if (current === undefined) return null;
  if (current === null) {
    return (
      <main className="sk-main">
        <section className="sk-card sk-center">
          <h2>{mode.kind === 'woodpecker' ? 'Cyclus af!' : 'Op voor nu'}</h2>
          <p className="sk-dim">
            {mode.kind === 'woodpecker'
              ? 'De volgende cyclus begint wanneer je wilt — probeer hem in de helft van de tijd.'
              : mode.kind === 'mistakes'
                ? mode.gameId
                  ? 'Alle fouten uit deze partij gehad.'
                  : 'Al je fouten van nu zijn geoefend. Ze komen terug als ze aan de beurt zijn.'
                : 'Geen puzzels meer in dit thema op jouw niveau. Kies een ander thema of train gemengd.'}
          </p>
          <button className="btn btn-primary" onClick={onStop}>
            Klaar
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="sk-main">
      <p className="sk-tally" aria-live="polite">
        {label}
        {tally.done > 0 && ` · ${tally.solved}/${tally.done} goed`}
      </p>
      <Solve
        key={`${step}:${current.p.id}`}
        puzzle={current.p}
        rating={profile.r}
        rated={mode.kind === 'train' && !current.mistake && !current.review && !state.cards[current.p.id]}
        context={current.mistake ? { mistake: current.mistake, game } : undefined}
        reason={current.mistake ? (reason ?? state.cards[current.mistake.id]?.reason ?? null) : null}
        onReason={(r) => current.mistake && void saveReason(current.mistake.id, r)}
        ratingGames={profile.g}
        onDone={(o, s) => void finish(current, o, s)}
        onNext={() => {
          setStep((n) => n + 1);
          setReason(null);
          setCurrent(undefined);
        }}
      />
    </main>
  );
}

function mergeCycle(cycles: readonly WoodpeckerCycle[], mine: WoodpeckerCycle | undefined): WoodpeckerCycle[] {
  if (!mine) return [...cycles];
  const same = (c: WoodpeckerCycle) => c.set_no === mine.set_no && c.cycle === mine.cycle;
  const theirs = cycles.find(same);
  if (theirs && theirs.position >= mine.position) return [...cycles];
  return [...cycles.filter((c) => !same(c)), mine];
}

/* ---------------- one puzzle ---------------- */

type Phase =
  | { kind: 'solving' }
  | { kind: 'wrong'; played: string; playedUci: string; expected: string }
  | { kind: 'solved' }
  | { kind: 'shown' };

function Solve({
  puzzle,
  rating,
  rated,
  ratingGames,
  context,
  reason,
  onReason,
  onDone,
  onNext,
}: {
  puzzle: Puzzle;
  rating: number;
  rated: boolean;
  ratingGames: number;
  /** Set when the puzzle is a position from his own game. */
  context?: { mistake: MistakeRecord; game: GameRecord | undefined };
  reason: MistakeReason | null;
  onReason: (r: MistakeReason) => void;
  onDone: (o: Outcome, seconds: number) => void;
  onNext: () => void;
}) {
  const start = useMemo(() => startPuzzle(puzzle), [puzzle]);
  const [s, setS] = useState<PuzzleState>(start);
  const [selected, setSelected] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'solving' });
  const [hint, setHint] = useState<string | null>(null);
  const [showLook, setShowLook] = useState(false);
  const [promo, setPromo] = useState<{ from: string; to: string } | null>(null);
  const [delta, setDelta] = useState<number | null>(null);
  const [solution, setSolution] = useState<string[] | null>(null);
  const started = useRef(Date.now());
  const ended = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const end = (solved: boolean) => {
    if (ended.current) return;
    ended.current = true;
    const counted = solved && !hint;
    let next: number | undefined;
    if (rated) {
      next = updateRating(rating, ratingGames, puzzle.rating, counted);
      setDelta(next - rating);
    }
    onDone({ solved: counted, newRating: next ?? null }, (Date.now() - started.current) / 1000);
  };

  const move = (from: string, to: string, promotion?: string) => {
    const uci = `${from}${to}${promotion ?? ''}`;
    const v = playMove(puzzle, s, uci);
    setSelected(null);
    setHint(null);
    if (v.kind === 'illegal') return;
    if (v.kind === 'wrong') {
      setPhase({ kind: 'wrong', played: v.played, playedUci: uci, expected: v.expected });
      end(false);
      return;
    }
    if (v.kind === 'solved') {
      setS(v.state);
      setPhase({ kind: 'solved' });
      end(true);
      return;
    }
    // Show the learner's own move, then the reply a beat later.
    const mid = new Chess(s.fen);
    const own = mid.move({ from, to, promotion });
    setS({ ...s, fen: mid.fen(), lastMove: [own.from, own.to] });
    timer.current = setTimeout(() => setS(v.state), 450);
  };

  const onSquare = (sq: string) => {
    if (phase.kind !== 'solving') return;
    const chess = new Chess(s.fen);
    if (chess.turn() !== s.color) return; // the reply is still on its way
    const piece = chess.get(sq as never);
    if (selected) {
      if (sq === selected) return setSelected(null);
      if (targetsOf(s.fen, selected).includes(sq)) {
        if (isPromotion(s.fen, selected, sq)) return setPromo({ from: selected, to: sq });
        return move(selected, sq);
      }
    }
    setSelected(piece && piece.color === s.color ? sq : null);
  };

  // Play the rest of the line on the board, one move at a time.
  const showSolution = () => {
    setSolution(solutionFrom(puzzle, s));
    setPhase({ kind: 'shown' });
    const chess = new Chess(s.fen);
    const uci = puzzle.moves.slice(s.ply);
    let i = 0;
    const step = () => {
      const m = uci[i];
      if (!m) return;
      const mv = chess.move({ from: m.slice(0, 2), to: m.slice(2, 4), promotion: m[4] });
      setS((prev) => ({ ...prev, fen: chess.fen(), lastMove: [mv.from, mv.to] }));
      i++;
      timer.current = setTimeout(step, 700);
    };
    timer.current = setTimeout(step, 300);
  };

  const theme = themeOf(puzzle.theme);
  const myTurn = phase.kind === 'solving';
  // A position from his own game, once the attempt is over, is reviewed on its own board.
  const review = !!context && !myTurn;

  // A wrong move in a puzzle: his move red, the right one green, on the position he faced.
  const expectedUci = puzzle.moves[s.ply];
  const arrows: Arrow[] =
    phase.kind === 'wrong' && expectedUci
      ? [
          { from: expectedUci.slice(0, 2), to: expectedUci.slice(2, 4), tone: 'good' },
          { from: phase.playedUci.slice(0, 2), to: phase.playedUci.slice(2, 4), tone: 'bad' },
        ]
      : [];

  const verdict = (
    <p className="sk-prompt" aria-live="polite">
      {phase.kind === 'solving' && (
        <>
          <span className={`sk-side is-${s.color}`} aria-hidden />
          {s.color === 'w' ? 'Wit' : 'Zwart'} aan zet — vind de beste zet
        </>
      )}
      {phase.kind === 'solved' && (
        <span className="sk-good">
          Goed!{delta != null && <span className="sk-delta is-up"> {delta >= 0 ? '+' : ''}{delta}</span>}
        </span>
      )}
      {phase.kind === 'wrong' && (
        <span className="sk-bad">
          {phase.played} is het niet{delta != null && <span className="sk-delta is-down"> {delta}</span>}
        </span>
      )}
      {phase.kind === 'shown' && (context ? <span>Zo zat het</span> : <span>Oplossing: {solution?.join(' ')}</span>)}
    </p>
  );

  return (
    <section className="sk-solve">
      {verdict}
      {!context && phase.kind === 'wrong' && (
        <p className="sk-dim sk-explain">
          Beter: <strong>{phase.expected}</strong> — {explainSan(phase.expected)}
        </p>
      )}
      {!context && phase.kind === 'shown' && solution?.[0] && (
        <p className="sk-dim sk-explain">
          {solution[0]}: {explainSan(solution[0])}
        </p>
      )}

      {review ? (
        <MistakeReview
          mistake={context!.mistake}
          game={context!.game}
          orientation={s.color}
          reason={reason}
          onReason={onReason}
        />
      ) : (
        <Board
          fen={s.fen}
          orientation={s.color}
          selected={selected}
          targets={selected ? targetsOf(s.fen, selected) : []}
          lastMove={s.lastMove}
          check={checkedKing(s.fen)}
          hint={hint}
          arrows={arrows}
          disabled={!myTurn}
          onSquare={onSquare}
        />
      )}

      {myTurn ? (
        <div className="sk-actions">
          <button className="btn btn-quiet" onClick={() => setShowLook((v) => !v)} aria-expanded={showLook}>
            Waar let ik op?
          </button>
          <button
            className="btn btn-quiet"
            onClick={() => {
              const h = hintOf(puzzle, s);
              if (h) setHint(h[0]);
            }}
          >
            Hint
          </button>
          <button
            className="btn btn-quiet"
            onClick={() => {
              end(false);
              if (!context) showSolution();
              else setPhase({ kind: 'shown' });
            }}
          >
            Geef op
          </button>
        </div>
      ) : (
        <div className="sk-actions">
          {phase.kind === 'wrong' && !context && (
            <button className="btn" onClick={showSolution}>
              Speel de oplossing
            </button>
          )}
          <button className="btn btn-primary sk-next" onClick={onNext} autoFocus>
            Volgende
          </button>
        </div>
      )}

      {showLook && myTurn && (
        <p className="sk-look">
          Eerst: wat deed de laatste zet van de ander, en wat staat er onverdedigd?
          {theme && <> {theme.look}</>}
        </p>
      )}
      {hint && <p className="sk-dim">Het stuk op {hint} moet zetten. (Telt niet als in één keer goed.)</p>}

      {promo && (
        <Sheet title="Promoveren tot" onClose={() => setPromo(null)}>
          <div className="sk-promo">
            {(['q', 'r', 'b', 'n'] as const).map((t) => (
              <button
                key={t}
                className="sk-promo-btn"
                aria-label={pieceName(s.color, t)}
                onClick={() => {
                  const { from, to } = promo;
                  setPromo(null);
                  move(from, to, t);
                }}
              >
                <img src={`/schaak/pieces/${s.color}${t.toUpperCase()}.svg`} alt="" />
              </button>
            ))}
          </div>
        </Sheet>
      )}
    </section>
  );
}

/* ---------------- the summary ---------------- */

export function Summary({ summary, state, onDone }: { summary: SessionSummary; state: SchaakState; onDone: () => void }) {
  const now = new Date();
  const solved = summary.attempts.filter((a) => a.solved).length;
  const delta = summary.ratingTo - summary.ratingFrom;
  const streak = streakDays(state.days, localDay(now));
  const tomorrow = dueWithin(state.cards, now, 24);
  return (
    <main className="sk-main">
      <section className="sk-card sk-summary">
        <h2>{solved === summary.attempts.length ? 'Alles goed!' : 'Goed geoefend'}</h2>
        <div className="sk-stats">
          <div>
            <span className="sk-stat-value">
              {solved}/{summary.attempts.length}
            </span>
            <span className="sk-stat-label">goed</span>
          </div>
          <div>
            <span className={`sk-stat-value${delta > 0 ? ' is-up' : delta < 0 ? ' is-down' : ''}`}>
              {delta > 0 ? '+' : delta < 0 ? '−' : '±'}
              {Math.abs(delta)}
            </span>
            <span className="sk-stat-label">rating ({summary.ratingTo})</span>
          </div>
          <div>
            <span className="sk-stat-value">{streak}</span>
            <span className="sk-stat-label">{streak === 1 ? 'dag op rij' : 'dagen op rij'}</span>
          </div>
        </div>
        <ol className="sk-attempts" aria-label="Deze sessie">
          {summary.attempts.map((a, i) => (
            <li key={i} className={a.solved ? 'is-good' : 'is-bad'}>
              <span aria-label={a.solved ? 'goed' : 'fout'}>{a.solved ? '✓' : '×'}</span> {a.label}
            </li>
          ))}
        </ol>
        <p className="sk-dim">
          {tomorrow > 0
            ? `Morgen staan er ${plural(tomorrow, 'herhaling', 'herhalingen')} klaar — wat je mist, komt terug tot het zit.`
            : 'Wat je mist, komt vanzelf terug tot het zit.'}
        </p>
        <button className="btn btn-primary" onClick={onDone}>
          Klaar
        </button>
      </section>
    </main>
  );
}
